import {ChangeDetectionStrategy, Component, computed, ErrorHandler, inject, resource, signal} from '@angular/core';
import {Button} from "primeng/button";
import {PageTitleComponent} from "../../../components/page-title/page-title.component";
import {Message} from "primeng/message";
import {Skeleton} from "primeng/skeleton";
import {UserService} from "../../../services/user.service";
import {MessageService} from "primeng/api";
import {Clipboard} from "@capacitor/clipboard";
import {isNative} from "../../../utils";
import {HousingConnectAuthDto} from "homey-housing-lottery-app-dto";
import {PageSubtitleLayoutComponent} from "../../../components/page-subtitle-layout/page-subtitle-layout.component";
import {firstValueFrom} from "rxjs";
import {AnalyticsService} from "../../../services/analytics.service";
import {ConfigService} from "../../../services/config.service";
import {LoadFailedComponent} from "../../../components/load-failed/load-failed.component";

@Component({
  selector: 'homey-account-housing-connect',
  imports: [
    Button,
    PageTitleComponent,
    Message,
    Skeleton,
    PageSubtitleLayoutComponent,
    LoadFailedComponent
  ],
  templateUrl: './account-housing-connect.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountHousingConnectComponent {
  private readonly userService = inject(UserService);
  private readonly messageService = inject(MessageService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly configService = inject(ConfigService);
  private readonly errorHandler = inject(ErrorHandler);

  protected readonly passwordVisible = signal(false);

  private readonly isActive = computed(() => this.userService.user()?.status === 'active');

  protected readonly authResource = resource<HousingConnectAuthDto | undefined, { readonly isActive: boolean }>({
    params: () => ({isActive: this.isActive()}),
    loader: async ({params}) => {
      if (!params.isActive) {
        return undefined;
      }
      try {
        return await firstValueFrom(this.userService.getHousingConnectAuth());
      } catch (error) {
        this.errorHandler.handleError(error);
        throw error;
      }
    }
  });

  // value() throws while the resource is in error.
  protected readonly auth = computed(() => this.authResource.hasValue() ? this.authResource.value() : undefined);
  protected readonly loading = this.authResource.isLoading;
  protected readonly isMaintenance = computed(() => !this.configService.nycAccountAccessEnabled());
  protected readonly hasError = computed(() => !!this.authResource.error());
  protected readonly retryLoad = async () => this.authResource.reload();
  protected readonly hasAuth = computed(() => !!this.auth());
  protected readonly housingConnectUrl = 'https://housingconnect.nyc.gov/PublicWeb/';
  protected readonly user = this.userService.signedInUser;

  protected togglePasswordVisibility(): void {
    this.passwordVisible.update(current => {
      const next = !current;
      if (next) {
        void this.analyticsService.sendEvent('housing-connect-password-got');
      }
      return next;
    });
  }

  protected async copy(field: 'username' | 'password'): Promise<void> {
    const auth = this.auth();
    if (!auth || !auth[field]) {
      return;
    }

    const text = auth[field];

    if (isNative) {
      await Clipboard.write({string: text});
    } else if (typeof navigator !== 'undefined' && navigator.clipboard) {
      await navigator.clipboard.writeText(text);
    }

    this.messageService.add({
      severity: 'success',
      summary: 'Copied',
      detail: `${field === 'username' ? 'Username' : 'Password'} copied to clipboard`
    });

    void this.analyticsService.sendEvent('housing-connect-password-got', {field});
  }

  protected getMaskedPassword(): string {
    const auth = this.auth();
    if (!auth?.password) {
      return '********';
    }

    return '*'.repeat(Math.max(auth.password.length, 8));
  }
}
